export const MAX_FILE_MB = 100

const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024

export interface PageRangeParseError {
  code: 'empty' | 'invalid_format' | 'out_of_range' | 'reversed_range' | 'empty_result'
}

export type PageRangeParseResult =
  | { ok: true; indices: number[]; remainingPages: number }
  | { ok: false; error: PageRangeParseError }

export function validateFileSize(file: File): boolean {
  return file.size <= MAX_FILE_BYTES
}

export function deletedPagesFilename(originalName: string): string {
  const dotIndex = originalName.lastIndexOf('.')
  const base = dotIndex !== -1 ? originalName.slice(0, dotIndex) : originalName
  return `${base}-bez-stranic.pdf`
}

export function parsePagesToDelete(spec: string, totalPages: number): PageRangeParseResult {
  const trimmed = spec.trim()
  if (!trimmed) return { ok: false, error: { code: 'empty' } }

  const result = new Set<number>()
  for (const part of trimmed.split(',')) {
    const piece = part.trim()
    if (!piece) continue

    const rangeMatch = piece.match(/^(\d+)\s*-\s*(\d+)$/)
    if (rangeMatch) {
      const from = Number(rangeMatch[1])
      const to = Number(rangeMatch[2])
      if (from < 1 || to < 1 || from > totalPages || to > totalPages) {
        return { ok: false, error: { code: 'out_of_range' } }
      }
      if (from > to) {
        return { ok: false, error: { code: 'reversed_range' } }
      }
      for (let i = from; i <= to; i++) result.add(i - 1)
      continue
    }

    if (!/^\d+$/.test(piece)) {
      return { ok: false, error: { code: 'invalid_format' } }
    }

    const pageNumber = Number(piece)
    if (pageNumber < 1 || pageNumber > totalPages) {
      return { ok: false, error: { code: 'out_of_range' } }
    }
    result.add(pageNumber - 1)
  }

  if (result.size === 0) {
    return { ok: false, error: { code: 'empty' } }
  }

  const remainingPages = totalPages - result.size
  if (remainingPages <= 0) {
    return { ok: false, error: { code: 'empty_result' } }
  }

  return {
    ok: true,
    indices: Array.from(result).sort((a, b) => a - b),
    remainingPages,
  }
}

export async function readPdfPageCount(file: File): Promise<number> {
  const { PDFDocument } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes)
  return pdfDoc.getPageCount()
}

export async function deletePdfPages(
  file: File,
  deleteIndices: number[],
): Promise<{ blob: Blob; totalPages: number; removedPages: number; remainingPages: number }> {
  const { PDFDocument } = await import('pdf-lib')
  const bytes = await file.arrayBuffer()
  const pdfDoc = await PDFDocument.load(bytes)
  const totalPages = pdfDoc.getPageCount()
  const deleteSet = new Set(deleteIndices)

  for (let i = totalPages - 1; i >= 0; i--) {
    if (deleteSet.has(i)) {
      pdfDoc.removePage(i)
    }
  }

  const out = await pdfDoc.save()
  return {
    blob: new Blob([out], { type: 'application/pdf' }),
    totalPages,
    removedPages: deleteSet.size,
    remainingPages: totalPages - deleteSet.size,
  }
}
